// Desktop projekce — odvodí z lineárního navigačního stacku + WM storu vykreslitelný
// model plochy: docked kaskáda (vrstvy bez stackId) + floating skupiny (grupované dle stackId).
//
// Interaktivita: živé je top okno KAŽDÉHO stacku (plocha i každá floating skupina).
// Výjimka: list-detail root — list (vlevo) i viditelný detail (vpravo) jsou živé zároveň.

import type { WindowChromeData } from '@/shared/legacyMagicBridge';
import type {
  AgendaRegistry,
  LayerDescriptor,
  LayerStatus,
  NavigationState,
  StoredAgendaConfig,
  WindowKind,
} from '@/shared/navigation/core';
import { resolveTitle } from '@/shared/navigation/shell';

import {
  stackKeyOf,
  type WindowMode,
  type WindowAnim,
  type WindowRect,
  type WindowUiState,
} from './windowManagerTypes';

/** Breadcrumb segment okna */
export interface DesktopCrumb {
  readonly layerId: string;
  readonly title: string;
}

/** Role docked okna v list-detail layoutu */
export type DetailRole = 'list' | 'detail' | 'overlay';

/** Jedno okno v desktop projekci */
export interface DesktopWindow {
  readonly layer: LayerDescriptor;
  readonly layerId: string;
  readonly agendaId: string;
  readonly kind: WindowKind;
  readonly title: string;
  /** Lucide/tree ikon name agendy (z jejího configu) — zobrazí se v typovém chipu hlavičky. */
  readonly icon?: string;
  /** undefined = plocha (docked kaskáda) */
  readonly stackId: string | undefined;
  /** Klíč skupiny pro z-order (stackId nebo PLOCHA_STACK_KEY). */
  readonly stackKey: string;
  /** Index okna v rámci své skupiny (0 = základní; vyšší = výš v kaskádě/z). */
  readonly withinIndex: number;
  readonly mode: WindowMode;
  /**
   * Zamýšlená velikost legacy dialogu (px), pokud se okno NEmá zobrazit přes celou plochu.
   * Nastaveno jen pro legacy docked okno, které přes `window/chrome` reportlo `maximized:false`
   * + rozměry. Phoenix ho podle toho vykreslí menší a vycentrované (viz computeRect).
   */
  readonly dialog?: { readonly w: number; readonly h: number };
  /** Geometrie floating/maximized okna (docked se měří z plochy). */
  readonly rect?: WindowRect;
  /** Probíhající spring animace (detach/dock). */
  readonly anim?: WindowAnim;
  readonly status: LayerStatus;
  /** Role v list-detail layoutu (jen pro docked okna list-detail rootu). */
  readonly detailRole?: DetailRole;
  /** Skryté okno (minimalizované) — zůstává namountované. */
  readonly hidden: boolean;
  /** První okno své skupiny (root plochy / základní okno floating skupiny). */
  readonly isBase: boolean;
  /** Okno je živé (interaktivní). */
  readonly isInteractive: boolean;
  /** Globálně fokusované okno (jen pro vizuální zvýraznění). */
  readonly isGlobalActive: boolean;
  /** Cesta od základu skupiny k tomuto oknu (včetně). */
  readonly breadcrumb: readonly DesktopCrumb[];
}

/** Skupina oken sdílejících stackId (plocha = stackId undefined) */
export interface DesktopStackGroup {
  readonly stackId: string | undefined;
  readonly windows: readonly DesktopWindow[];
  readonly baseLayerId: string;
  readonly topLayerId: string;
}

/** Výsledná projekce pro vykreslení plochy */
export interface DesktopProjection {
  /** Okna plochy (stackId === undefined) v pořadí stacku; root první. */
  readonly plocha: readonly DesktopWindow[];
  /** Floating skupiny grupované dle stackId (pořadí dle prvního výskytu). */
  readonly floatingGroups: readonly DesktopStackGroup[];
  /** Všechna okna v jednom seznamu (pořadí stacku). */
  readonly allWindows: readonly DesktopWindow[];
  /** Lookup okna podle layerId (pro render v lineárním pořadí). */
  readonly byLayerId: ReadonlyMap<string, DesktopWindow>;
  /** Root agenda běží v list-detail režimu (list vlevo, detail vpravo). */
  readonly isListDetail: boolean;
  /** Globálně fokusované okno. */
  readonly activeLayerId: string | null;
  /** Počet oken v ploše (pro patičku/taskbar). */
  readonly dockedCount: number;
}

interface DeriveInput {
  readonly state: NavigationState;
  readonly windows: Readonly<Record<string, WindowUiState>>;
  readonly activeLayerId: string | null;
  readonly registry?: AgendaRegistry;
  /** „Vycucnutý" chrome legacy oken (per layerId) — zdroj velikosti/maximalizace dialogů. */
  readonly chromeByLayer?: Readonly<Record<string, WindowChromeData>>;
  /** Dynamický NÁZEV native oken (per layerId) — publikuje agenda detailu přes `useWindowTitle`. */
  readonly titleByLayer?: Readonly<Record<string, string>>;
}

/**
 * Intrinsic velikost legacy „dialogu" — okno v ploše (bez stackId), které přes `window/chrome`
 * reportlo `maximized:false` + kladné rozměry. Vrací velikost NEZÁVISLE na WM mode (dialog smí být
 * i maximalizovaný = přes celý stack — to řeší mode). Platí pro plochu a roli overlay / žádnou roli
 * (ne list/detail — ty mají vlastní zónu).
 */
function resolveDialogSize(
  kind: WindowKind,
  stackId: string | undefined,
  detailRole: DetailRole | undefined,
  chrome: WindowChromeData | undefined
): { readonly w: number; readonly h: number } | undefined {
  if (kind !== 'legacy' || stackId !== undefined) return undefined;
  if (detailRole !== undefined && detailRole !== 'overlay') return undefined;
  if (!chrome || chrome.maximized !== false) return undefined;
  const w = chrome.width;
  const h = chrome.height;
  if (typeof w !== 'number' || w <= 0 || typeof h !== 'number' || h <= 0) return undefined;
  return { w, h };
}

function resolveKind(config: StoredAgendaConfig | undefined): WindowKind {
  return config?.presentationHints?.windowKind ?? 'native';
}

/**
 * Efektivní režim okna. Zdrojem pravdy pro docked-vs-floating je `stackId` (kernel),
 * ne WM store — ten se po reloadu neobnovuje. Bez stackId = docked; se stackId = floating,
 * pokud WM neříká maximized/minimized.
 */
function effectiveMode(stackId: string | undefined, raw: WindowMode | undefined): WindowMode {
  if (stackId === undefined) return 'docked';
  return raw === 'maximized' || raw === 'minimized' ? raw : 'floating';
}

interface ListDetailClassification {
  readonly roleById: ReadonlyMap<string, DetailRole>;
  readonly interactiveIds: ReadonlySet<string>;
}

/**
 * Klasifikuje docked vrstvy plochy do rolí list-detail layoutu:
 * root = list; vrstva hned za ním se `zone: 'detail'` = detail panel vpravo; všechny ostatní =
 * overlay (kaskáda přes celý layout). Zónu razítkuje `openDetail` (výběr v listu), a protože ta
 * před otevřením zavře docked vrstvy nad rootem, je detail vždy nejvýš jeden.
 * Živé: list, detail a vrchní overlay.
 */
function classifyListDetail(bucket: readonly LayerDescriptor[]): ListDetailClassification {
  const roleById = new Map<string, DetailRole>();
  const interactiveIds = new Set<string>();
  const root = bucket[0];
  if (!root) return { roleById, interactiveIds };

  roleById.set(root.layerId, 'list');
  interactiveIds.add(root.layerId);

  let firstOverlayIndex = 1;
  const detail = bucket[1];
  if (detail?.zone === 'detail') {
    roleById.set(detail.layerId, 'detail');
    interactiveIds.add(detail.layerId);
    firstOverlayIndex = 2;
  }

  for (let i = firstOverlayIndex; i < bucket.length; i++) {
    const layer = bucket[i];
    if (!layer) continue;
    roleById.set(layer.layerId, 'overlay');
    if (i === bucket.length - 1) interactiveIds.add(layer.layerId);
  }

  return { roleById, interactiveIds };
}

export function deriveDesktopProjection(input: DeriveInput): DesktopProjection {
  const { state, windows, activeLayerId, registry, chromeByLayer, titleByLayer } = input;
  const layers = state.layers;

  // list-detail detekce z root vrstvy PLOCHY (ta definuje layout viewportu).
  // Osamostatněný (floating) root nesmí přebít layout plochy.
  const firstRoot = layers.find((l) => l.stackId === undefined && l.presentation === 'root');
  const rootConfig = firstRoot ? registry?.getById(firstRoot.agendaId) : undefined;
  const isListDetail = rootConfig?.presentationHints?.shell?.layoutMode === 'list-detail';

  // 1) Rozděl vrstvy do skupin dle stackId (pořadí výskytu v lineárním stacku).
  const groupOrder: string[] = [];
  const groupLayers = new Map<string, LayerDescriptor[]>();
  for (const layer of layers) {
    const key = stackKeyOf(layer.stackId);
    let bucket = groupLayers.get(key);
    if (!bucket) {
      bucket = [];
      groupLayers.set(key, bucket);
      groupOrder.push(key);
    }
    bucket.push(layer);
  }

  const allWindows: DesktopWindow[] = [];
  const byLayerId = new Map<string, DesktopWindow>();
  const groups: DesktopStackGroup[] = [];

  for (const key of groupOrder) {
    const bucket = groupLayers.get(key) ?? [];
    if (bucket.length === 0) continue;
    const baseLayer = bucket[0];
    const topLayer = bucket[bucket.length - 1];
    if (!baseLayer || !topLayer) continue;

    const isPlocha = key === stackKeyOf(undefined);
    const listDetail = isPlocha && isListDetail ? classifyListDetail(bucket) : null;

    const groupWindows: DesktopWindow[] = [];
    const crumbAccumulator: DesktopCrumb[] = [];

    bucket.forEach((layer, withinIndex) => {
      const config = registry?.getById(layer.agendaId);
      const ui = windows[layer.layerId];
      const detailRole = listDetail?.roleById.get(layer.layerId);
      const kind = resolveKind(config);
      const chrome = chromeByLayer?.[layer.layerId];
      const staticTitle = resolveTitle(layer, config); // popisek TYPU okna (např. „Detail seriálu")
      // NÁZEV okna (titulek v hlavičce + taskbar záložky osamostatněných oken + historie) =
      // dynamický název objektu. Legacy ho reportuje přes `window/chrome`, native přes `useWindowTitle`
      // (titleByLayer). `||` (ne `??`) drží legacy sémantiku: prázdný chrome.title = fallback na typ.
      const runtimeTitle = kind === 'legacy' ? chrome?.title : titleByLayer?.[layer.layerId];
      const title = runtimeTitle || staticTitle;
      // BREADCRUMB = TYP okna: native vždy statický popisek (dynamický název by drobečky jen
      // duplikoval a znečitelnil navigaci zpět); legacy beze změny (obecný typ u něj neznáme).
      const crumbTitle = kind === 'legacy' ? title : staticTitle;
      crumbAccumulator.push({ layerId: layer.layerId, title: crumbTitle });

      const dialog = resolveDialogSize(kind, layer.stackId, detailRole, chrome);
      // Dialog (legacy malé okno v ploše) smí být v ploše i maximalizovaný (přes celý stack)
      // nebo přesunutý/zvětšený (vlastní rect) — režim bere z WM storu; jinak docked (vycentrovaný).
      // Ostatní okna: docked iff bez stackId, jinak floating (effectiveMode).
      const mode = dialog
        ? ui?.mode === 'maximized'
          ? 'maximized'
          : 'docked'
        : effectiveMode(layer.stackId, ui?.mode);
      const isInteractive = listDetail
        ? listDetail.interactiveIds.has(layer.layerId)
        : layer.layerId === topLayer.layerId;
      const hidden = mode === 'minimized';

      const win: DesktopWindow = {
        layer,
        layerId: layer.layerId,
        agendaId: layer.agendaId,
        kind,
        title,
        ...(config?.icon ? { icon: config.icon } : {}),
        stackId: layer.stackId,
        stackKey: key,
        withinIndex,
        mode,
        ...(dialog ? { dialog } : {}),
        ...(ui?.rect ? { rect: ui.rect } : {}),
        ...(ui?.anim ? { anim: ui.anim } : {}),
        status: layer.status,
        ...(detailRole ? { detailRole } : {}),
        hidden,
        isBase: layer.layerId === baseLayer.layerId,
        isInteractive,
        isGlobalActive: layer.layerId === activeLayerId,
        breadcrumb: crumbAccumulator.slice(),
      };
      groupWindows.push(win);
      allWindows.push(win);
      byLayerId.set(win.layerId, win);
    });

    groups.push({
      stackId: bucket[0]?.stackId,
      windows: groupWindows,
      baseLayerId: baseLayer.layerId,
      topLayerId: topLayer.layerId,
    });
  }

  const plochaGroup = groups.find((g) => g.stackId === undefined);
  const floatingGroups = groups.filter((g) => g.stackId !== undefined);
  const plocha = plochaGroup?.windows ?? [];

  return {
    plocha,
    floatingGroups,
    allWindows,
    byLayerId,
    isListDetail,
    activeLayerId,
    dockedCount: plocha.length,
  };
}
